import React from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './oilApi';
import { OilIcon } from './OilIcons';
import { SVC_LORE, pct } from './oilMafiaText';
import { sfxClose } from './oilSounds';

const Block = ({ title, children, testid }) => (
  <div className="mt-2 rounded-xl border border-zinc-800 bg-black/50 p-2.5" data-testid={testid}>
    <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">{title}</p>
    <div className="space-y-1 text-[10px] leading-relaxed text-zinc-300">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex gap-1.5">
    <span className="shrink-0 text-amber-600">▸</span><span>{children}</span>
  </p>
);

export const OilServiceBriefing = ({ svc, limits = {}, onClose }) => {
  const info = SVC_LORE[svc.id] || {};
  const spy = !svc.offensive;
  const cap = limits.max_offensive_attacks_per_day;
  const perTarget = limits.max_offensive_attacks_per_target_per_day || 3;
  const imm = limits.target_immunity_after_hit_hours;
  const cargo = svc.cargo_steal;
  const fac = svc.facility_damage;

  return createPortal(
    <div className="fixed inset-0 z-[98] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="oil-svc-info-overlay" onClick={onClose}>
      <div className="w-full max-w-md p-3" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
          <div className="rounded-[16px] bg-[#0b0906] p-3.5" data-testid={`oil-svc-briefing-${svc.id}`}>
            <div className="mb-1 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">
                  Ficha do serviço
                </p>
                <p className="truncate text-xs font-black uppercase text-amber-200">{svc.name}</p>
              </div>
              <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-svc-info-close"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-700/70 bg-black/85 text-sm font-black text-amber-300 active:scale-90">✕</button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto pr-0.5">
              {info.tag && (
                <p className="text-center text-[9px] font-black uppercase tracking-[0.25em] text-rose-400">
                  {info.tag}
                </p>
              )}

              <Block title="O que é este trabalho sujo" testid="oil-svc-lore">
                <p className="italic text-zinc-200">{info.lore}</p>
              </Block>

              <Block title="Como funciona" testid="oil-svc-how">
                {spy ? (
                  <>
                    <Li>Chance de sucesso de <b className="text-amber-300">{pct(svc.success_probability)}</b> —
                      seus informantes voltam com o dossiê ou voltam de mãos vazias.</Li>
                    {(info.extra || []).map((t, i) => <Li key={i}>{t}</Li>)}
                  </>
                ) : (
                  <>
                    <Li>Dano de <b className="text-amber-300">{svc.damage_pct}% do FOX Dólar do alvo</b>, com
                      teto de <b className="text-amber-300">{fmt(limits.max_damage_per_attack_fox)}</b> por golpe.</Li>
                    <Li>Chance de sucesso de <b className="text-amber-300">{pct(svc.success_probability)}</b>. Se a
                      operação fracassar você perde só o pagamento do serviço — e o alvo nem fica sabendo.</Li>
                    <Li>Se o alvo tiver escudo, o <b className="text-sky-300">mais forte entra na frente</b>
                      {' '}(Ouro bloqueia 100%, Prata 60%, Bronze 30%) e é <b>destruído no confronto</b>, tenha
                      bloqueado ou não.</Li>
                    <Li>Acertando, o FOX Dólar do alvo é <b className="text-red-400">queimado</b> (não cai no seu
                      bolso — some do jogo) e ele fica <b className="text-amber-300">{imm}h imune</b> a novos golpes.</Li>
                    <Li>O alvo recebe um recado nos <b>Informantes</b> com o seu nome e o serviço usado.</Li>
                    {cargo && (                      <>
                        <Li><b className="text-amber-300">ROUBO DE CARGA:</b> acertando, você leva
                          {' '}<b className="text-amber-300">{cargo.barrel_pct}% dos barris prontos</b> do galpão dele
                          {' '}(teto de <b className="text-amber-300">{cargo.max_barrels_per_hit} barris por golpe</b>),
                          {' '}começando pelos de <b>menor nível</b>. Os barris entram no seu galpão e podem ser vendidos.</Li>
                        <Li><b className="text-amber-300">CARGA NOBRE ({pct(cargo.premium_cargo_probability)}):</b> o
                          caminhão estava com o melhor — a ordem inverte e o roubo começa pelos
                          {' '}<b>barris de maior nível</b>.</Li>
                        <Li>Se o galpão dele estiver vazio, o caminhão levava só o bruto:
                          {' '}<b className="text-sky-300">{cargo.fallback_drops_pct}% das gotas</b> dele
                          {' '}(teto de <b className="text-sky-300">{fmt(cargo.max_drops_per_hit)}</b>).</Li>
                        <Li>Teto de <b className="text-amber-300">{cargo.daily_barrels_per_target} barris por alvo por dia</b>,
                          somando todos os atacantes. Batido o teto, o golpe ainda dá dano e Respeito, mas não sai carga.</Li>
                        <Li>Escudo que bloqueia impede tudo: <b className="text-sky-300">nenhum barril e nenhuma gota saem</b>.</Li>
                      </>
                    )}
                    {fac && (
                      <>
                        <Li><b className="text-orange-300">DANO NA INSTALAÇÃO:</b> acertando, a
                          {' '}<b className="text-orange-300">{fac.facility === 'refinery' ? 'Usina de Perfuração' : 'Envasadora'}</b>
                          {' '}dele perde <b className="text-orange-300">{fac.points} de integridade</b> (de 100) —
                          sempre no <b>nível mais alto</b> que ele tem.</Li>
                        <Li>Chegando a <b className="text-red-400">0, aquela instalação para</b>: ele não
                          {fac.facility === 'refinery' ? ' extrai' : ' envasa'} naquele nível até
                          {' '}<b>pagar o reparo</b>. Três acertos quebram.</Li>
                        <Li>Acima de 0 a instalação funciona normal — dano não reduz rendimento, e ele
                          não perde gotas, barris nem FOX Dólar por causa do amassado.</Li>
                        <Li>Teto de <b className="text-orange-300">{limits.max_daily_facility_damage_per_target} pontos
                          de dano em instalação por alvo por dia</b>, somando todos os atacantes. Batido o teto,
                          o golpe ainda queima FOX Dólar e paga Respeito.</Li>
                        <Li>Escudo que bloqueia <b className="text-sky-300">protege a instalação</b>: golpe
                          bloqueado não amassa nada.</Li>
                      </>
                    )}
                    <Li>Consome <b className="text-amber-300">1</b> das suas <b className="text-amber-300">{cap}</b>
                      {' '}operações ofensivas do dia. No mesmo alvo você pode bater
                      até <b className="text-amber-300">{perTarget}x por dia</b> — mas o Respeito cai a cada
                      repetida (2º golpe metade, 3º zero).</Li>
                  </>
                )}
              </Block>

              <Block title="Valores e recompensas" testid="oil-svc-gains">
                <Li>Custo do serviço: <b className="text-amber-300"><OilIcon k="black" /> {fmt(svc.cost_black_coins)} Black Coins</b>
                  {' '}— pago na contratação, mesmo que a operação falhe.</Li>
                {spy ? (
                  <Li>Sucesso: dossiê completo do alvo na tela
                    {' '}+ <b className="text-rose-300">{fmt(svc.respect_success)} de Respeito</b>.</Li>
                ) : (
                  <>
                    <Li>Acerto em cheio: <b className="text-rose-300">+{fmt(svc.respect_hit)} de Respeito</b>
                      {' '}e o prejuízo do alvo no seu histórico de Rixas.</Li>
                    <Li>Escudo do alvo segurou: <b className="text-rose-300">+{fmt(svc.respect_blocked)} de Respeito</b>
                      {' '}pela ousadia — e ele perde o escudo de qualquer forma.</Li>
                  </>
                )}
                <Li>Respeito é o que te leva para a <b className="text-amber-300">Lista dos Mais Procurados</b>
                  {' '}(o Top 3 ganha cartaz na parede e prêmio em moedas no fim da Season).</Li>
              </Block>

              <Block title="Riscos e letras miúdas" testid="oil-svc-risks">
                <Li>Fracasso: <b className="text-zinc-100">0 de Respeito</b> e o pagamento do serviço vai embora.</Li>
                {!spy && (
                  <>
                    <Li><b className="text-amber-300">Império pequeno demais é intocável</b> — e o tamanho
                      soma o FOX Dólar com os <b>barris prontos no galpão</b>, então guardar carga sem
                      vender não tira ninguém da lista de alvos.</Li>
                    <Li>Nenhum alvo pode perder mais de
                      {' '}<b className="text-amber-300">{limits.max_daily_damage_pct_of_fox}% do FOX Dólar dele por dia</b>;
                      o que passar disso não é aplicado.</Li>
                    <Li>Anti-farm: 2º golpe no mesmo alvo no dia rende <b>metade</b> do Respeito, do 3º em diante
                      rende <b>zero</b>. Teto de <b>600 de Respeito por dia</b>.</Li>
                    <Li>Nas últimas horas da Season entra a <b className="text-blue-300">Trégua do Petróleo</b> e
                      nenhum serviço ofensivo pode ser contratado.</Li>
                  </>
                )}
              </Block>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OilServiceBriefing;
